import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EditPanelNewService } from '../../../../../views/pages/dynamic-dashboard/edit-panel-new/edit-panel-new.service';
import { UserService } from '../../../../../core/auth/_services/user.service';
import { FacilityService } from '../../../../../core/configuration/_services/facility.service';
import { UnitService } from '../../../../../core/configuration/_services/unit.service';
import { SystemService } from '../../../../../core/configuration/_services/system.service';
import { WidgetDashboardService } from '../../../../../core/widget-dashboard/widget-dashboard.service';

@Component({
  selector: 'kt-filter-form-input-popup',
  templateUrl: './filter-form-input-popup.component.html',
  styleUrls: ['./filter-form-input-popup.component.scss']
})
export class FilterFormInputPopupComponent implements OnInit {

  constructor(
    private readonly dialogRef: MatDialogRef<FilterFormInputPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private editPanelNewService: EditPanelNewService,
    private fb: UntypedFormBuilder,
    private userService: UserService,
    private facilityService: FacilityService,
    private unitService: UnitService,
    private systemService: SystemService,
    private widgetDashboardService: WidgetDashboardService,
    private cdr: ChangeDetectorRef,
  ) { }
  group: UntypedFormGroup;

  // Stable option arrays for kt-select — never bind [options] to a method that
  // builds a new array each call (infinite change-detection loop).
  operatorOptions = [{ label: 'AND', value: 'AND' }, { label: 'OR', value: 'OR' }];
  columnOptions: { label: string; value: any; tooltip?: string }[] = [];
  private readonly checkOptionsMap = new WeakMap<object, { label: string; value: any }[]>();
  private readonly EMPTY_OPTIONS: { label: string; value: any }[] = [];
  private loadingSubFilters = new Set<any>();

  isSubFilterLoading(subFilter: any): boolean {
    return this.loadingSubFilters.has(subFilter);
  }

  ngOnInit(): void {
    this.group = this.cloneFormGroup(this.data.filterGroup);
    this.buildColumnOptions();
  }

  buildColumnOptions() {
    const collectionName = this.group.get('targetCollection')?.value;
    const attribute = this.group.get("attribute")?.value;
    const opts = this.getFieldsForCollection(collectionName, attribute)
      .map(f => ({ label: f.displayName, value: f.name, tooltip: f.displayName }));
    if (this.group.get('calc')?.value) {
      const label = this.group.get('label').value;
      opts.push({ label, value: label, tooltip: label });
    }
    this.columnOptions = opts;
  }

  private titlecase(value: string): string {
    return (value ?? '').toString().replace(/\b\w/g, c => c.toUpperCase());
  }

  private fieldForSubFilter(subFilter: any) {
    const collectionName = this.group.get('targetCollection').value;
    const column = subFilter?.get('column')?.value;
    const collectionConfig = this.editPanelNewService.targetCollectionList.find(col => col.name === collectionName);
    return collectionConfig?.fields.find(f => f.name == column);
  }

  // Check (operator) options depend on the subfilter's selected column, so they are
  // cached per-subfilter control (WeakMap survives filter add/remove reindexing) and
  // rebuilt on column change — returning a stable reference each CD cycle.
  buildCheckOptions(subFilter: any) {
    const field = this.fieldForSubFilter(subFilter);
    const operators = this.group.get('calc').value
      ? ['<', '>', '=', '<=', '>=', 'between']
      : (field?.operators ?? []);
    const isDate = !!field?.isDateField;
    const opts = operators.map(op => ({ label: isDate ? this.getCheckName(op) : this.titlecase(op), value: op }));
    this.checkOptionsMap.set(subFilter, opts);
  }

  getCheckOptions(subFilter: any) {
    if (!this.checkOptionsMap.has(subFilter)) this.buildCheckOptions(subFilter);
    return this.checkOptionsMap.get(subFilter);
  }

  // Value-list options are the field's own `values` array — a stable instance
  // (reassigned only in onColumnSelect), so binding directly is loop-safe.
  getValueOptions(subFilter: any) {
    const field = this.fieldForSubFilter(subFilter);
    return field?.values ?? this.EMPTY_OPTIONS;
  }

  timeRanges = this.getTimeRanges();

  getTimeRanges() {
    let items = this.widgetDashboardService.timeRange.map((item) => {
      return { id: item.id, name: item.name };
    })
    //adding 2 new items to the time range list
    const index = items.findIndex(item => item.name === 'Previous Month');
    items.splice(index + 1, 0,
      { id: 20, name: 'This Quarter' },
      { id: 21, name: 'Previous Quarter' }
    );
    return items;
  }

  cloneFormGroup(group: UntypedFormGroup): UntypedFormGroup {
    const newGroup = new UntypedFormGroup({});
    Object.keys(group.controls).forEach(key => {
      const control = group.get(key);
      if (control instanceof UntypedFormControl) {
        newGroup.addControl(key, new UntypedFormControl(control.value, control.validator, control.asyncValidator));
      } else if (control instanceof UntypedFormGroup) {
        newGroup.addControl(key, this.cloneFormGroup(control)); // Recursively clone nested FormGroups
      } else if (control instanceof UntypedFormArray) {
        newGroup.addControl(key, this.cloneFormArray(control)); // Handle FormArray separately
      }
    });
    return newGroup;
  }

  cloneFormArray(array: UntypedFormArray): UntypedFormArray {
    const newArray = new UntypedFormArray([]);
    array.controls.forEach(control => {
      if (control instanceof UntypedFormGroup) {
        newArray.push(this.cloneFormGroup(control));
      } else {
        newArray.push(new UntypedFormControl(control.value, control.validator, control.asyncValidator));
      }
    });
    return newArray;
  }

  filterData(data, column) {
    switch (column) {
      case 'owner': return data.filter(d => this.userService.userList.find(u => u._id == d));
      case 'facility': return data.filter(d => this.facilityService.facilityList.find(f => f._id == d));
      case 'unit': return data.filter(d => this.unitService.unitList.find(u => u._id == d));
      case 'system': return data.filter(d => this.systemService.systemList.find(s => s._id == d));
      default: return data;
    }
  }

  getLabel(id: string, field, collection) {
    return this.editPanelNewService.getLabel(id, field.name, collection)
  }

  onColumnSelect(event, collectionName, group?, i?, j?) {
    let subFilter;
    if (group) {
      subFilter = group.get('filters.filter')?.at(i)?.get('subFilter')?.at(j)
      subFilter?.get('values')?.reset();
    }
    // kt-select's (selectionChange) emits the value directly (not a MatSelectChange).
    const column = typeof event === 'object' && event !== null ? event.value : event;
    if (subFilter) this.buildCheckOptions(subFilter);
    let collectionConfig = this.editPanelNewService.targetCollectionList.find(col => col.name === collectionName)
    if (collectionConfig) {
      let field = collectionConfig.fields.find(f => f.name == column);
      if (field?.categorical && (!field?.values || !field?.values.length)) {
        if (subFilter) this.loadingSubFilters.add(subFilter);
        this.editPanelNewService.getFieldDistinctValues(collectionName, column).subscribe({
          next: (res: any) => {
          if (res?.data && res?.data[column]) {
            let values = this.filterData(res?.data[column], column);
            field.values = values.map(i => {
              let label = this.getLabel(i, field, collectionName);
              label = label === null ? 'null' : label;
              return { value: i, label: label };
            });
          }
          },
          error: () => {
            if (subFilter) this.loadingSubFilters.delete(subFilter);
            this.cdr.markForCheck();
          },
          complete: () => {
            if (subFilter) this.loadingSubFilters.delete(subFilter);
            this.cdr.markForCheck();
          }
        });
      }
    }
  }

  getFieldsForCollection(collectionName, attribute?) {
    let collectionConfig = this.editPanelNewService.targetCollectionList.find(col => col.name === collectionName)
    return collectionConfig ? collectionConfig.fields.filter(field => !field.prePopulateBaseFilter && !field.attributeOnly && (!field.scopedToAttribute || field.scopedToAttribute === attribute)) : []
  }

  getFieldOperatorsForCollection(collectionName, group, i, j) {
    if (group.get('calc').value) return ['<', '>', '=', '<=', '>=', 'between']
    const column = group.get('filters.filter')?.at(i)?.get('subFilter')?.at(j)?.get('column')?.value
    let collectionConfig = this.editPanelNewService.targetCollectionList.find(col => col.name === collectionName)
    if (collectionConfig) {
      let field = collectionConfig.fields.find(f => f.name == column);
      return field?.operators ? field.operators : []
    }
    return []
  }

  getCheckName(operator){
    let operatorMap = {
      '<': 'Before Set Date',
      '>': 'After Set Date',
      '=': 'On Set Date',
      'between': 'Date Range',
    }
    return operatorMap[operator]
  }

  isInputType(collectionName, group, i, j) {
    const betweenOperator = group.get('filters.filter')?.at(i)?.get('subFilter')?.at(j)?.get('check')?.value == 'between'
    const column = group.get('filters.filter')?.at(i)?.get('subFilter')?.at(j)?.get('column')?.value
    let collectionConfig = this.editPanelNewService.targetCollectionList.find(col => col.name === collectionName)
    if (collectionConfig) {
      let field = collectionConfig.fields.find(f => f.name == column);
      if (betweenOperator && field?.isDateField ) return 'dateRange'
      if (betweenOperator && group.get('calc').value) return 'between'
      const inputType = (field?.categorical || field?.values?.length) ? 'list' : field?.isDateField ? 'date' : 'input';
      return inputType;
    }
    return 'input';
  }

  isDateRangeValid(subFilter: UntypedFormGroup): boolean {
    const from = subFilter.get('range.from')?.value;
    const to = subFilter.get('range.to')?.value;
    if (!(from instanceof Date) || isNaN(from.getTime())) {
      return false;
    }
    if (!(to instanceof Date) || isNaN(to.getTime())) {
      return false;
    }
    return from.getTime() <= to.getTime();
  }


  pickTimeRange(timeObj, subFilterControl) {
    let rangeControl = subFilterControl.get('range') as UntypedFormGroup;
    subFilterControl.get('values').setValue(''); // Reset the values control for any old value
    let value = rangeControl.value
    if(timeObj.id == 99){
      timeObj.name = new Date(rangeControl.get('from').value).toLocaleDateString() + ' - ' + new Date(rangeControl.get('to').value).toLocaleDateString()
    }
    rangeControl.get('custom').setValue(timeObj)
    rangeControl.patchValue({
      custom: timeObj,
      from: timeObj.id == 99 ? value.from : '',
      to: timeObj.id == 99 ? value.to : ''
    })
  }

  isMultipleSelect(group, i, j) {
    const check = group.get('filters.filter')?.at(i)?.get('subFilter')?.at(j)?.get('check')?.value;
    const res = ['in', 'not in'].includes((check ?? '').toString().toLowerCase());
    return res;
  }

  onCheckOptionClick(operator, group, i, j) {
    if (this.isInputType(group.get('targetCollection').value, group, i, j) === 'list') {
      const valueControl = group.get('filters.filter')?.at(i)?.get('subFilter')?.at(j)?.get('values');
      if (this.isMultipleSelect(group, i, j)) {
        // Add it to array
        const value = valueControl?.value;
        if (!Array.isArray(value)) {
          valueControl?.setValue([value]);
        }
      } else {
        // Single select, clear it
        valueControl?.setValue('');
      }
    }
  }

  addSubFilter(filterIndex: number) {
    const form = this.group;
    const filtersGroup = form.get('filters') as UntypedFormGroup;
    const filterArray = filtersGroup.get('filter') as UntypedFormArray;
    const subFilterArray = filterArray.at(filterIndex).get('subFilter') as UntypedFormArray;
    subFilterArray.push(this.createSubFilter());
  }

  createSubFilter(): UntypedFormGroup {
    let group = this.fb.group({
      column: [this.group.get('calc').value ? this.group.get('label').value : '', Validators.required],
      check: ['', Validators.required],
      values: [''],
      range: this.fb.group({
        custom: [{}],
        from: [''],
        to: ['']
      }),
      rangeType: [''],
      disabled: [false]
    }, { validators: this.editPanelNewService.validateGroupOperatorForCorrectValues() });
    return group;
  }

  removeSubFilter(filterIndex: number, subFilterIndex: number) {
    const form = this.group;
    const filtersGroup = form.get('filters') as UntypedFormGroup;
    const filterArray = filtersGroup.get('filter') as UntypedFormArray;
    const subFilterArray = filterArray.at(filterIndex).get('subFilter') as UntypedFormArray;
    subFilterArray.removeAt(subFilterIndex);
    if (subFilterArray.length == 0)
      this.removeFilter(filterIndex);
  }

  removeFilter(index: number) {
    const form = this.group;
    const filtersGroup = form.get('filters') as UntypedFormGroup;
    const filterArray = filtersGroup.get('filter') as UntypedFormArray;
    filterArray.removeAt(index);
  }

  getSubfilterDeleteTooltip(disabled, key) {
    let defaultTooltip = 'Remove Sub filter'
    const form = this.group;
    let collection = form.get('targetCollection').value
    let attribute = form.get('attribute').value
    if (!attribute) return defaultTooltip

    let collectionConfig = this.editPanelNewService.targetCollectionList.find(col => col.name === collection)
    let fieldConfig = collectionConfig.fields.find(field => field.name == attribute)
    if (!fieldConfig?.prePopulateBaseFilter) return defaultTooltip

    let prepopulatedFilter = fieldConfig?.prePopulateBaseFilter.find(pf => pf.key == key)
    return prepopulatedFilter?.msg && disabled ? prepopulatedFilter.msg : defaultTooltip
  }

  addFilter() {
    const form = this.group;
    const filtersGroup = form.get('filters') as UntypedFormGroup;
    const filterArray = filtersGroup.get('filter') as UntypedFormArray;
    filterArray.push(this.createFilter());
  }

  createFilter(forPreselect?): UntypedFormGroup {
    let control = this.fb.group({
      subOperator: ['AND'],
      subFilter: this.fb.array(forPreselect ? [] : [this.createSubFilter()])
    });
    return control;
  }

  applyFilter() {
    this.data.filterGroup.setControl('filters', this.group.get('filters'));
    this.dialogRef.close();
  }

  clearFilter() {
    const filterArray = this.group.get('filters').get('filter') as UntypedFormArray;
    filterArray.clear();
  }

}
